import { cloneDeep } from 'lodash';
import { timeParse, timeFormat } from 'd3';
import { Context } from '../index';

export const aggregateStats = (
  context: Context,
  {
    allStats,
    granularity,
    xTicks,
    merchantPerformance = false,
  }: { allStats: any; granularity: any; xTicks: any; merchantPerformance: any },
) => {
  /*
   * Create buckets that represent the time period between each set of 2 ticks.
   * As we iterate through the stats, we place them in the bucket they belong
   * in by comparing dates. Then, aggregate each bucket of stats together.
   */
  const aggregateStats: any = [];

  allStats.forEach(obj => {
    const dateBuckets = xTicks.map(tick => {
      return {
        date: tick,
        stats: [],
      };
    });

    const stats = obj.data;
    let bucketIndex = 0;

    for (let i = 0; i < stats.length; i++) {
      const currentDate = dateBuckets[bucketIndex].date;
      const nextDate = dateBuckets[bucketIndex + 1] ? dateBuckets[bucketIndex + 1].date : null;
      const currentDateComparison = context.actions.date.compareDates({
        d1: currentDate,
        d2: stats[i].event_date,
      });
      const nextDateComparison = context.actions.date.compareDates({
        d1: nextDate,
        d2: stats[i].event_date,
      });

      if (currentDateComparison <= 0 && (nextDateComparison > 0 || !nextDate)) {
        dateBuckets[bucketIndex].stats.push(stats[i]);
      } else {
        bucketIndex++;
        i--;
      }
    }
    aggregateStats.push(
      context.actions.graph.aggregateByDate({
        buckets: dateBuckets,
        merchantPerformance,
      }),
    );
  });

  return allStats.map((obj, i) => {
    obj.data = aggregateStats[i];
    return obj;
  });
};

export const aggregateByDate = (
  context: Context,
  { buckets, merchantPerformance }: { buckets: any; merchantPerformance: any },
) => {
  const formatDate = timeFormat('%Y-%m-%d');
  const statArray = buckets.map(bucket => {
    if (!bucket.stats.length) return null;

    const { stats } = bucket;
    const aggregateStat = key =>
      stats.reduce((agg, stat) => {
        return (agg += parseFloat(stat[key]));
      }, 0);
    if (merchantPerformance) {
      const revenueKey = stats[0] && stats[0].attributed_revenue ? 'attributed_revenue' : 'revenue';
      return {
        event_date: formatDate(bucket.date),
        [revenueKey]: aggregateStat(revenueKey),
        spend: aggregateStat('spend'),
        clicks: aggregateStat('clicks'),
        rpc: aggregateStat('revenue') / aggregateStat('clicks'),
        roi_actual: parseFloat(String(aggregateStat('roi_actual') / stats.length)).toFixed(1),
      };
    }
    return {
      pub_id: stats[0].pub_id,
      event_date: formatDate(bucket.date),
      clicks: aggregateStat('clicks'),
      publisher_total_earnings: aggregateStat('publisher_total_earnings'),
      total_attributed_revenue: aggregateStat('total_attributed_revenue'),
    };
  });
  return statArray.filter(data => data);
};

export const getTicks = (
  context: Context,
  { granularity, dateFrom, dateTo }: { granularity: any; dateFrom: any; dateTo: any },
) => {
  /*
   * By determining the x-axis ticks in megatable-graph,
   * we can more clearly aggregate the stats
   */
  const xTicks = [dateFrom];
  const offsetUnit = granularity === 'daily' ? 'days' : `${granularity.slice(0, -2)}s`;
  const parseDate = timeParse('%Y-%m-%d');

  let date = context.actions.date.offsetFromDate({
    originalDate: dateFrom,
    number: 1,
    unit: offsetUnit,
  });

  // Continue to add ticks until we've exceeded our date range
  while (context.actions.date.compareDates({ d1: date, d2: dateTo }) <= 0) {
    const adjustedDate = context.actions.graph.getGranularityStartDate({
      dateObject: cloneDeep(date),
      granularity,
    });
    xTicks.push(adjustedDate);
    date = context.actions.date.offsetFromDate({ originalDate: date, number: 1, unit: offsetUnit });
  }

  return xTicks.map(date => parseDate(date));
};

export const getGranularityStartDate = (
  context: Context,
  { dateObject, granularity }: { dateObject: any; granularity: any },
) => {
  // weekly => week, monthly => month, yearly => year
  const timeUnit = granularity.slice(0, -2);
  return context.actions.date.formatDate({ dateObj: dateObject.startOf(timeUnit).startOf('day') });
};

export const processStats = (
  context: Context,
  {
    stats,
    ticks,
    selectedGranularity,
    dateRange,
    option,
  }: { stats: any; ticks: any; selectedGranularity: any; dateRange: any; option: any },
) => {
  // Backend returns all monthly stats rounded down to the 1st, replace with correct date
  if (['monthly', 'yearly'].includes(selectedGranularity)) {
    const formattedDate = dateRange.date_from;
    const truncatedDateFrom = formattedDate.slice(0, 7);
    const truncatedStatDate = stats[0].event_date.slice(0, 7);

    if (truncatedDateFrom === truncatedStatDate) {
      // Ex: 2019-04-01 becomes 2019-04-08
      stats[0].event_date = formattedDate;
    }
  }

  let processedStats = [{ data: stats, color: option.color }];
  if (['weekly', 'yearly'].includes(selectedGranularity)) {
    processedStats = context.actions.graph.aggregateStats({
      allStats: processedStats,
      granularity: selectedGranularity,
      xTicks: ticks,
      merchantPerformance: true,
    });
  }
  return processedStats;
};

export const processEditGraphStats = (
  context: Context,
  {
    respData,
    ticks,
    selectedGranularity,
    dateRange,
  }: { respData: any; ticks: any; selectedGranularity: any; dateRange: any },
) => {
  let processedStats = respData.map(obj => {
    if (['monthly', 'yearly'].includes(selectedGranularity)) {
      const formattedDate = dateRange.date_from;
      const truncatedDateFrom = formattedDate.slice(0, 7);
      const truncatedStatDate = obj.data[0].event_date.slice(0, 7);

      if (truncatedDateFrom === truncatedStatDate) {
        // Ex: 2019-04-01 becomes 2019-04-08
        obj.data[0].event_date = formattedDate;
      }
    }
    return obj;
  });

  if (['weekly', 'yearly'].includes(selectedGranularity)) {
    processedStats = context.actions.graph.aggregateStats({
      allStats: processedStats,
      granularity: selectedGranularity,
      xTicks: ticks,
      merchantPerformance: true,
    });
  }
  return processedStats;
};
